import { IPromise } from 'angular'
import moment from 'moment'
import { VdTracking } from '@vend/vend.js'
import { ApiRegister, ApiResponse } from '@vend/types'

import { CASH_MOVEMENT_TYPES } from '../../../constants'
import { extractData } from '../../../util/extract-data'
import { $http, $q } from '../../../ng-compat/imports'

const REGISTERS_END_POINT = '/api/2.0/registers'
const REGISTER_OPEN_SEQUENCES_END_POINT = '/api/2.0/register_open_sequences'

interface RegisterClosure {
  register: ApiRegister
  payments: ClosurePayment[]
  note?: string
}

interface ClosurePayment {
  payment_type_id: string
  totalCounted: string
}

interface PaymentsSummary {
  register_open_time: string
  register_closure_number: number
  payments: Payment[]
}

interface Payment {
  payment_type_id: string
  payment_type_name: string
  total: string
}

interface ApiCashMovement {
  id: string
  amount: number
  date: string
  movement_type: string
  note: string | null
  register_id: string
  register_open_sequence_id: string
  type: CASH_MOVEMENT_TYPES
  user_id: string
  user: {
    id: string
    name: string
  }
}

interface ApiCashTotals {
  cash_sales_total: number
  movements_total: number
}

interface CashMovement {
  amount: number
  date: string
  note?: string
  register_open_sequence_id: string
  type: CASH_MOVEMENT_TYPES
  user_id: string
}

/**
 * Resource for querying the registers API for register data.
 */
export class RegistersResource {
  constructor(private readonly vdTracking: VdTracking) {
    'ngInject'
  }

  /**
   * Open the given register.
   *
   * @param  register
   *         The register to open
   *
   * @return A promise resolved with the updated register details once the register has been opened
   */
  public open(register: ApiRegister): IPromise<ApiRegister> {
    const registerId = register && register.id

    if (typeof registerId !== 'string') {
      return $q.reject(
        'Unable to construct register open query with register: ' + register
      )
    }

    const data = {
      register_open_time: moment.utc().format(),
    }

    return $http
      .put<ApiResponse<ApiRegister>>(
        `${REGISTERS_END_POINT}/${registerId}/actions/open`,
        data
      )
      .then(extractData)
  }

  /**
   * Get the payments summary information for the current register closure of the given register.
   *
   * @param  register
   *         The register to get the payments summary information for
   *
   * @return A promise resolved with the payments summary of the current register closure
   */
  public getPaymentsSummary(register: ApiRegister): IPromise<PaymentsSummary> {
    const registerId = register && register.id

    if (typeof registerId !== 'string') {
      return $q.reject(
        'Unable to construct payments summary query with register: ' + register
      )
    }

    return $http
      .get<ApiResponse<PaymentsSummary>>(
        `${REGISTERS_END_POINT}/${registerId}/payments_summary`
      )
      .then(extractData)
  }

  /**
   * Post the given register closure to close the associated register.
   *
   * @param  registerClosure
   *         The register closure to post
   *
   * @return A promise resolved with the updated register details once the register has been closed
   */
  public close(registerClosure: RegisterClosure): IPromise<ApiRegister> {
    const registerId = registerClosure.register.id

    const data = {
      note: registerClosure.note,
      payments: registerClosure.payments.map(payment => ({
        payment_type_id: payment.payment_type_id,
        total: payment.totalCounted,
      })),
    }

    return $http
      .put<ApiResponse<ApiRegister>>(
        `${REGISTERS_END_POINT}/${registerId}/actions/close`,
        data
      )
      .then(extractData)
  }

  /**
   * Records cash movement transactions. Used for recording Cash in, Cash out, Petty cash in and Petty cash out.
   *
   * @param cashMovement
   *        The cash movement to record
   *
   * @return A promise resolved with the cash movement has been recorded.
   */
  public recordCashMovement(
    cashMovement: CashMovement
  ): IPromise<ApiCashMovement> {
    return $http
      .post<ApiResponse<ApiCashMovement>>(
        `${REGISTER_OPEN_SEQUENCES_END_POINT}/${cashMovement.register_open_sequence_id}/cash_movements`,
        cashMovement
      )
      .then(response => {
        this.vdTracking.trackUserEvent({
          eventName: 'Cash Management',
          metadata: {
            'Cash Movement Type': cashMovement.type,
          },
        })
        return extractData(response)
      })
  }

  /**
   * Retrieves the sum of all cash movements.
   *
   * @param  register
   *
   * @return A promise resolved with the cash totals have been retrieved.
   */
  public getCashTotals(register: ApiRegister): IPromise<ApiCashTotals> {
    return $http
      .get<ApiResponse<ApiCashTotals>>(
        `${REGISTER_OPEN_SEQUENCES_END_POINT}/${register.register_open_sequence_id}/cash_totals`
      )
      .then(extractData)
  }

  /**
   * Retrieves a log of cash movements for a register.
   *
   * @param  config
   *         Additional options and criteria to influence the results.
   *
   *         @param register_open_sequence_id
   *                The registers opening sequence id.
   *
   *         @param [type]
   *                The type of cash movement, 'Opening float', 'Closing float' etc.
   *
   * @return A promise resolved with the cash movements have been retrieved.
   */
  public getCashMovements(config: {
    register_open_sequence_id: string
    type?: string
  }): IPromise<ApiCashMovement[]> {
    return $http
      .get<ApiResponse<ApiCashMovement[]>>(
        `${REGISTER_OPEN_SEQUENCES_END_POINT}/${config.register_open_sequence_id}/cash_movements`,
        { params: { type: config.type } }
      )
      .then(extractData)
  }

  /**
   * Update a register with the provided data.
   *
   * @param  register
   *         The register to be updated
   *
   * @param  data
   *         The values to be updated, e.g { button_layout_id: layoutId }
   *
   * @return A promise resolved once the register has been updated
   */
  public updateRegister(
    register: ApiRegister,
    data: {
      button_layout_id: string
      is_quick_keys_enabled?: boolean
    }
  ): IPromise<ApiRegister> {
    return $http
      .put<ApiResponse<ApiRegister>>(
        `${REGISTERS_END_POINT}/${register.id}`,
        data
      )
      .then(extractData)
  }
}
